import React, { useEffect, useState } from 'react';

import { ECompanyType, getCompanies, ICompany } from '~/src/dash/api/company';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { obfuscateCompanyName } from '~/src/dash/utils/obfuscate';

// component handles the api call and generation of menu list for companies
// on top of the MaterialSearchDropdownMulti
interface IProps {
  onChange: (companyIDs: number[]) => void;
  publisherID: string;
  companyIDs: number[];
  // default false
  disabled?: boolean;
  // default true
  enableMultiSelect?: boolean;
  companyTypesAllowed?: ECompanyType[];
  openAddEditCompanyModelHandler?: () => void;
  // when the counter is incremented in the parent, refresh the api
  counter: number;
  menuTitle?: string;
  maxHeight?: number;
  username: string;
}

export const CompanyMultiSelect = function ({
  onChange,
  disabled = false,
  companyIDs = [],
  publisherID,
  enableMultiSelect = true,
  companyTypesAllowed = [],
  openAddEditCompanyModelHandler,
  counter,
  menuTitle = 'Companies',
  maxHeight,
  username,
}: IProps) {
  const [companyMenuList, setCompanyMenuList] = useState<IMenuList>({});
  async function loadCompanies() {
    try {
      let companies: ICompany[] = await getCompanies(publisherID).then(
        (companies: ICompany[]) => companies,
      );
      if (companyTypesAllowed.length > 0) {
        companies = companies.filter((company: ICompany) =>
          companyTypesAllowed.includes(company.CompanyType),
        );
      }
      setCompanyMenuList(buildCompanyMenuList(companies, companyIDs, username));
    } catch (e) {
      setCompanyMenuList({});
    }
  }

  function handleMenuSelectionDone(values: IMenuList) {
    const companyIDs = Object.values(values)
      .filter((src: IListItem) => {
        return src.selected;
      })
      .map((src: IListItem) => parseInt(src.ID, 10) || 0);
    onChange(companyIDs);
  }

  useEffect(() => {
    loadCompanies();
  }, [publisherID, counter, companyIDs]);

  return (
    <MaterialSearchDropdownMulti
      getValueFromSearchElement={(src: IListItem) => src.title}
      selectAll={enableMultiSelect}
      menuTitle={menuTitle}
      list={companyMenuList}
      disabled={disabled}
      onChange={setCompanyMenuList}
      handleMenuAcceptClick={handleMenuSelectionDone}
      isNoneSelectedAllSelected={false}
      enableMultiSelect={enableMultiSelect}
      width='400'
      maxHeight={maxHeight || 300}
      topListItemText={openAddEditCompanyModelHandler ? 'Add/Edit Company' : ''}
      topListItemClickHandler={openAddEditCompanyModelHandler}
    />
  );
};

function buildCompanyMenuList(
  companies: ICompany[],
  selectedIDs: number[],
  username: string,
): IMenuList {
  const list = {} as IMenuList;
  companies.forEach((company) => {
    list[company.ID] = {
      title: obfuscateCompanyName(company, username, company.Name),
      ID: company.ID.toString(),
      selected: selectedIDs.includes(company.ID),
    } as IListItem;
  });
  return list;
}
